import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import type {
  Location,
  PaymentMethod,
  SalaryPreviewItem,
  GenerateSalariesResponse,
} from '../../types';
import { PaymentMethodLabels } from '../../types';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
  }).format(amount);
};

interface SalaryGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
}

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

const months = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchSalaryPreview = async (
  year: number,
  month: number,
  locationId?: string
): Promise<SalaryPreviewItem[]> => {
  const params: Record<string, any> = { year, month };
  if (locationId) params.locationId = locationId;

  const response = await axiosInstance.post('/expenses/generate-salaries/preview', params);
  return response.data;
};

export const SalaryGeneratorDialog = ({ open, onClose }: SalaryGeneratorDialogProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [locationId, setLocationId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');

  // Fetch locations
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
    enabled: open,
  });

  // Fetch preview
  const {
    data: preview,
    isLoading: previewLoading,
  } = useQuery({
    queryKey: ['salary-preview', year, month, locationId],
    queryFn: () => fetchSalaryPreview(year, month, locationId || undefined),
    enabled: open,
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async (): Promise<GenerateSalariesResponse> => {
      const params: Record<string, any> = {
        year,
        month,
        paymentMethod,
      };
      if (locationId) params.locationId = locationId;

      const response = await axiosInstance.post('/expenses/generate-salaries', params);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['salary-preview'] });
      showMessage(
        `${data.generated} salaire(s) généré(s), ${data.skipped} ignoré(s)`,
        'success'
      );
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la génération';
      showMessage(message, 'error');
    },
  });

  // Summary calculations
  const summary = useMemo(() => {
    if (!preview) return { total: 0, toGenerate: 0, alreadyGenerated: 0 };

    const toGenerate = preview.filter((p) => !p.alreadyGenerated);
    const alreadyGenerated = preview.filter((p) => p.alreadyGenerated);
    const total = toGenerate.reduce((sum, p) => sum + p.proratedAmount, 0);

    return {
      total,
      toGenerate: toGenerate.length,
      alreadyGenerated: alreadyGenerated.length,
    };
  }, [preview]);

  const handleGenerate = async () => {
    if (summary.toGenerate === 0) {
      showMessage('Aucun salaire à générer', 'warning');
      return;
    }

    if (
      window.confirm(
        `Générer ${summary.toGenerate} salaire(s) pour un total de ${formatCurrency(summary.total)} ?`
      )
    ) {
      await generateMutation.mutateAsync();
    }
  };

  const isLoading = previewLoading || generateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Générer les salaires</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Année</InputLabel>
              <Select
                value={year}
                label="Année"
                onChange={(e) => setYear(e.target.value as number)}
                disabled={isLoading}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mois</InputLabel>
              <Select
                value={month}
                label="Mois"
                onChange={(e) => setMonth(e.target.value as number)}
                disabled={isLoading}
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Emplacement</InputLabel>
              <Select
                value={locationId}
                label="Emplacement"
                onChange={(e) => setLocationId(e.target.value)}
                disabled={isLoading}
              >
                <MenuItem value="">Tous</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Paiement</InputLabel>
              <Select
                value={paymentMethod}
                label="Paiement"
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                disabled={isLoading}
              >
                {Object.entries(PaymentMethodLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Summary */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${summary.toGenerate} à générer`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${summary.alreadyGenerated} déjà générés`}
            color="default"
            variant="outlined"
          />
          <Chip
            label={`Total: ${formatCurrency(summary.total)}`}
            color="success"
            variant="filled"
          />
        </Box>

        {/* Preview Table */}
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : preview && preview.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employé</TableCell>
                  <TableCell align="right">Salaire de base</TableCell>
                  <TableCell align="center">Jours travaillés</TableCell>
                  <TableCell align="right">Montant prorata</TableCell>
                  <TableCell align="center">Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((item) => (
                  <TableRow
                    key={item.userId}
                    sx={{
                      opacity: item.alreadyGenerated ? 0.5 : 1,
                      bgcolor: item.alreadyGenerated ? 'action.hover' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.userName}
                      </Typography>
                      {item.daysWorked < item.daysInPeriod && (
                        <Typography variant="caption" color="warning.main">
                          Début: {new Date(item.effectiveDate).toLocaleDateString('fr-FR')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.baseSalary)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={item.daysWorked < item.daysInPeriod ? 'warning.main' : 'inherit'}
                      >
                        {item.daysWorked} / {item.daysInPeriod}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={item.alreadyGenerated ? 'text.secondary' : 'error.main'}
                      >
                        {formatCurrency(item.proratedAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {item.alreadyGenerated ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : item.daysWorked < item.daysInPeriod ? (
                        <WarningIcon color="warning" fontSize="small" />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            Aucun employé avec une configuration de salaire trouvé pour cette période.
          </Alert>
        )}

        {summary.toGenerate > 0 &&
          preview?.some((p) => !p.alreadyGenerated && p.daysWorked < p.daysInPeriod) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Certains employés ont commencé en cours de mois. Le salaire sera calculé au prorata.
            </Alert>
          )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={isLoading || summary.toGenerate === 0}
          startIcon={generateMutation.isPending ? <CircularProgress size={16} /> : null}
        >
          Générer {summary.toGenerate} salaire(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
